import { useMemo } from "react";
import { MultiCombobox, type MultiComboboxOption } from "@/components/cost_index/MultiCombobox";
import type { FulfilmentIndexKey } from "../api";
import { canonicalize, channelLeaf, resolveForRender } from "./channelKeys";
import { ChannelPicker } from "./ChannelPicker";

export type FulfilmentScopeSelection = { tracking: string[]; variant: string[]; sku: string[]; channel: string[] };

const unique = (values: string[]) => [...new Set(values)].sort((a, b) => a.localeCompare(b));
const options = (values: string[]): MultiComboboxOption[] => unique(values).map((value) => ({ value, label: value }));
const matches = (selected: string[], value: string) => selected.length === 0 || selected.includes(value);

export function scopeRows(rows: FulfilmentIndexKey[], selection: FulfilmentScopeSelection): FulfilmentIndexKey[] {
  const leaves = rows.map(channelLeaf);
  const channels = new Set(resolveForRender(selection.channel.length ? selection.channel : ["all"], leaves));
  return rows.filter((row) => channels.has(channelLeaf(row))
    && matches(selection.tracking, row.product)
    && matches(selection.variant, row.variant)
    && matches(selection.sku, row.xero_sku));
}

export function resolvedProductVariants(rows: FulfilmentIndexKey[], selection: FulfilmentScopeSelection): Array<{ product: string; variant: string }> {
  const pairs = new Map(scopeRows(rows, { ...selection, sku: [] }).map((row) => [`${row.product}\0${row.variant}`, { product: row.product, variant: row.variant }]));
  return [...pairs.values()];
}

export function ScopePicker({ rows, selection, onChange, disabled = false }: {
  rows: FulfilmentIndexKey[]; selection: FulfilmentScopeSelection; onChange: (selection: FulfilmentScopeSelection) => void; disabled?: boolean;
}) {
  const leaves = rows.map(channelLeaf);
  const channelSet = useMemo(() => new Set(resolveForRender(selection.channel.length ? selection.channel : ["all"], leaves)), [leaves.join("|"), selection.channel.join("|")]);
  const inChannels = rows.filter((row) => channelSet.has(channelLeaf(row)));
  const productOptions = options(inChannels.map((row) => row.product));
  const variantRows = inChannels.filter((row) => matches(selection.tracking, row.product));
  const variantOptions = unique(variantRows.map((row) => row.variant)).map((value) => ({ value, label: value, hint: unique(variantRows.filter((row) => row.variant === value).map((row) => row.product)).join(", ") }));
  const skuRows = variantRows.filter((row) => matches(selection.variant, row.variant));
  const skuOptions = [...new Map(skuRows.map((row) => [row.xero_sku, { value: row.xero_sku, label: row.xero_sku, hint: `${row.product} · ${row.variant} · ${row.place_label}` }])).values()].sort((a, b) => a.label.localeCompare(b.label));
  const valid = (selected: string[], available: MultiComboboxOption[]) => { const allowed = new Set(available.map((item) => item.value)); return selected.filter((value) => allowed.has(value)); };

  const changeChannels = (channel: string[]) => {
    const nextChannelSet = new Set(canonicalize(channel, leaves));
    const nextRows = rows.filter((row) => nextChannelSet.has(channelLeaf(row)));
    const tracking = valid(selection.tracking, options(nextRows.map((row) => row.product)));
    const variants = options(nextRows.filter((row) => matches(tracking, row.product)).map((row) => row.variant));
    const variant = valid(selection.variant, variants);
    const skus = options(nextRows.filter((row) => matches(tracking, row.product) && matches(variant, row.variant)).map((row) => row.xero_sku));
    onChange({ channel, tracking, variant, sku: valid(selection.sku, skus) });
  };
  const changeTracking = (tracking: string[]) => {
    const nextVariants = options(inChannels.filter((row) => matches(tracking, row.product)).map((row) => row.variant));
    const variant = valid(selection.variant, nextVariants);
    const nextSkus = options(inChannels.filter((row) => matches(tracking, row.product) && matches(variant, row.variant)).map((row) => row.xero_sku));
    onChange({ ...selection, tracking, variant, sku: valid(selection.sku, nextSkus) });
  };
  const changeVariant = (variant: string[]) => {
    const nextSkus = options(inChannels.filter((row) => matches(selection.tracking, row.product) && matches(variant, row.variant)).map((row) => row.xero_sku));
    onChange({ ...selection, variant, sku: valid(selection.sku, nextSkus) });
  };

  return <div className="flex flex-wrap items-end gap-3">
    <ChannelPicker rows={rows} value={selection.channel} onChange={changeChannels} disabled={disabled} />
    <Picker label="Product"><MultiCombobox ariaLabel="Product" value={selection.tracking} onChange={changeTracking} options={productOptions} placeholder="Search products" emptyText="No matching product." disabled={disabled} nonMutating /></Picker>
    <Picker label="Variant"><MultiCombobox ariaLabel="Variant" value={selection.variant} onChange={changeVariant} options={variantOptions} placeholder="Search variants" emptyText="No matching variant." disabled={disabled} nonMutating /></Picker>
    <Picker label="Channel SKU" wide><MultiCombobox ariaLabel="Channel SKU" value={selection.sku} onChange={(sku) => onChange({ ...selection, sku })} options={skuOptions} placeholder="Search channel SKUs" emptyText="No matching channel SKU." disabled={disabled} nonMutating /></Picker>
  </div>;
}

function Picker({ label, children, wide = false }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return <div className={`grid ${wide ? "w-64" : "w-48"} max-w-full gap-1.5`}><span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</span>{children}</div>;
}
